import { baseApi } from "@/shared/api";
// Точечный импорт вместо барреля @/shared/utils: баррель тянет @/shared/hooks
// и роняет тесты этого модуля на циклической инициализации store.
import { fileResponseHandler, type FileResponse } from "@/shared/utils/responseHandlers";
import type { KanbanBoard } from "@/features/tasks";
import type {
  Project,
  ProjectMinimal,
  ProjectDetail,
  ProjectFormPayload,
  RedistributeBudgetPayload,
  RedistributeBudgetResponse,
} from "../types";
import { PLANNED_FIELDS } from "../utils/plannedFields";

export function buildFormData(payload: ProjectFormPayload): FormData {
  const formData = new FormData();

  if (payload.name !== undefined) formData.append("name", payload.name);
  if (payload.is_internal !== undefined) formData.append("is_internal", String(payload.is_internal));
  if (payload.income_sources !== undefined) {
    formData.append("income_sources", JSON.stringify(payload.income_sources));
  }
  if (payload.description !== undefined) formData.append("description", payload.description);
  if (payload.status !== undefined) formData.append("status", payload.status);
  PLANNED_FIELDS.forEach((field) => {
    const value = payload[field];
    if (value !== undefined && value !== "") formData.append(field, value);
  });
  if (payload.start_date !== undefined) formData.append("start_date", payload.start_date);
  if (payload.end_date !== undefined) formData.append("end_date", payload.end_date);
  if (payload.category !== undefined) formData.append("category", payload.category);
  if (payload.beneficiaries_coverage !== undefined && payload.beneficiaries_coverage !== null) {
    formData.append("beneficiaries_coverage", String(payload.beneficiaries_coverage));
  }
  if (payload.direct_beneficiaries !== undefined && payload.direct_beneficiaries !== null) {
    formData.append("direct_beneficiaries", String(payload.direct_beneficiaries));
  }
  if (payload.indirect_beneficiaries !== undefined && payload.indirect_beneficiaries !== null) {
    formData.append("indirect_beneficiaries", String(payload.indirect_beneficiaries));
  }
  if (payload.expected_results_text !== undefined) {
    formData.append("expected_results_text", payload.expected_results_text);
  }
  if (payload.goal !== undefined) {
    formData.append("goal", JSON.stringify(payload.goal));
  }
  if (payload.region_ids !== undefined) {
    formData.append("region_ids", JSON.stringify(payload.region_ids));
  }
  if (payload.partners !== undefined) {
    formData.append("partners", JSON.stringify(payload.partners));
  }
  if (payload.social_impact !== undefined && payload.social_impact !== null) {
    formData.append("social_impact", JSON.stringify(payload.social_impact));
  }
  if (payload.project_picture instanceof File) {
    formData.append("project_picture", payload.project_picture);
  }

  return formData;
}

function needsFormData(payload: ProjectFormPayload): boolean {
  return payload.project_picture instanceof File;
}

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], void>({
      query: () => ({
        url: "/api/projects/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "Projects", id: "LIST" }],
    }),
    getMyProjects: builder.query<Project[], void>({
      query: () => ({
        url: "/api/projects/my/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "Projects", id: "LIST" }],
    }),
    getProjectsByWorker: builder.query<ProjectMinimal[], number>({
      query: (workerId) => ({
        url: `/api/projects/by-worker/${workerId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, workerId) => [{ type: "ProjectMembers", id: `WORKER-${workerId}` }],
    }),
    getProject: builder.query<ProjectDetail, number>({
      query: (id) => ({
        url: `/api/projects/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "Projects", id }],
    }),
    createProject: builder.mutation<Project, ProjectFormPayload>({
      query: (payload) => {
        if (needsFormData(payload)) {
          return {
            url: "/api/projects/",
            method: "POST",
            body: buildFormData(payload),
            credentials: "include" as const,
          };
        }
        const { project_picture: _pic, ...jsonPayload } = payload;
        return {
          url: "/api/projects/",
          method: "POST",
          body: jsonPayload,
          credentials: "include" as const,
        };
      },
      invalidatesTags: [
        { type: "Projects", id: "LIST" },
        { type: "Organization" as const, id: "ME" },
        "PoolBalances",
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),
    updateProject: builder.mutation<Project, { id: number; data: ProjectFormPayload }>({
      query: ({ id, data }) => {
        if (needsFormData(data)) {
          return {
            url: `/api/projects/${id}/`,
            method: "PATCH",
            body: buildFormData(data),
            credentials: "include" as const,
          };
        }
        return {
          url: `/api/projects/${id}/`,
          method: "PATCH",
          body: data,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Projects", id: "LIST" },
        { type: "Projects", id },
        { type: "TaskBoard", id: "LIST" },
        { type: "AuditEntries", id: "PROJECT_LIST" },
        { type: "Organization" as const, id: "ME" },
        "PoolBalances",
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),
    redistributeBudget: builder.mutation<RedistributeBudgetResponse, RedistributeBudgetPayload>({
      query: (body) => ({
        url: "/api/projects/redistribute/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { source_project_id, target_project_id }) => [
        { type: "Projects", id: "LIST" },
        ...(source_project_id !== undefined ? [{ type: "Projects" as const, id: source_project_id }] : []),
        { type: "Projects", id: target_project_id },
        { type: "Organization" as const, id: "ME" },
        "PoolBalances",
        { type: "Budget" as const, id: "SUMMARY" },
        { type: "Budget" as const, id: "PROJECT_TABLE" },
        { type: "AuditEntries" as const, id: "PROJECT_LIST" },
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),
    getBoard: builder.query<KanbanBoard, number>({
      query: (projectId) => ({
        url: `/api/projects/${projectId}/board/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, projectId) => [
        { type: "Projects", id: `BOARD-${projectId}` },
        { type: "Tasks", id: "LIST" },
      ],
    }),
    downloadProjectDossier: builder.mutation<FileResponse, number>({
      query: (projectId) => ({
        url: `/api/projects/${projectId}/dossier/`,
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetMyProjectsQuery,
  useGetProjectsByWorkerQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useRedistributeBudgetMutation,
  useGetBoardQuery,
  useDownloadProjectDossierMutation,
} = projectsApi;
